import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FileText, Clock, CheckCircle, XCircle, IndianRupee, AlertCircle, TrendingDown, Eye, Edit, Trash2, ChevronDown, ChevronUp, Download, MoreVertical } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { toast } from 'react-hot-toast';
import { getApiErrorMessage } from '../../utils/apiErrors';
import ConfirmModal from '../../components/modals/ConfirmModal';
import ColumnFilter from '../../components/shared/ColumnFilter';
import ListPageHeader from '../../components/shared/ListPageHeader';
import { purchaseInvoicesAPI } from '../../services/part2-api';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const STATUS_CONFIG = {
  draft: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Draft' },
  unpaid: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Unpaid' },
  partially_paid: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Partial' },
  paid: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Paid' },
  overdue: { bg: 'bg-red-100', text: 'text-red-700', label: 'Overdue' },
  cancelled: { bg: 'bg-slate-100', text: 'text-slate-500', label: 'Cancelled' }
};

const InvoiceRow = ({ invoice, index, onView, onEdit, onRecordPayment, onCancel, onDelete, onDownload, bid, navigate }) => {
  const [expanded, setExpanded] = useState(false);
  const statusConfig = STATUS_CONFIG[invoice.status] || STATUS_CONFIG.draft;
  const isOverdue = invoice.due_date && invoice.due_date < new Date().toISOString().split('T')[0] && invoice.balance_due > 0;
  const displayStatus = isOverdue ? STATUS_CONFIG.overdue : statusConfig;

  return (
    <>
      <tr 
        className={`tl-tr hover:bg-teal-50/40 cursor-pointer ${index % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}
        onClick={() => setExpanded(!expanded)}
      >
        <td className="tl-td" style={{ width: '3%' }}>
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </td>
        <td className="tl-td" style={{ width: '10%' }}>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
              <FileText className="w-3.5 h-3.5 text-teal-600" />
            </div>
            <div>
              <p className="tl-cell-primary text-[12px]">{invoice.invoice_number}</p>
              {invoice.supplier_invoice_no && (
                <p className="text-[10px] text-slate-400">Vendor: {invoice.supplier_invoice_no}</p>
              )}
            </div>
          </div>
        </td>
        <td className="tl-td" style={{ width: '14%' }}>
          <p className="tl-cell-regular text-[12px] font-medium">{invoice.supplier_name}</p>
          {invoice.supplier_gstin && <p className="text-[10px] text-slate-400">{invoice.supplier_gstin}</p>}
        </td>
        <td className="tl-td" style={{ width: '9%' }}>
          <div>
            <p className="tl-cell-regular text-[12px]">
              {invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
            </p>
            <p className="text-[10px] text-slate-400">Invoice Date</p>
          </div>
        </td>
        <td className="tl-td" style={{ width: '9%' }}>
          <div>
            <p className={`tl-cell-regular text-[12px] ${isOverdue ? 'text-red-600 font-medium' : ''}`}>
              {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
            </p>
            <p className="text-[10px] text-slate-400">Due Date</p>
          </div>
        </td>
        <td className="tl-td tl-td-right" style={{ width: '10%' }}>
          <p className="font-semibold text-slate-800 text-[13px]">₹{(invoice.grand_total || 0).toLocaleString('en-IN')}</p>
        </td>
        <td className="tl-td tl-td-right" style={{ width: '9%' }}>
          <p className="text-[12px] text-emerald-600">₹{(invoice.amount_paid || 0).toLocaleString('en-IN')}</p>
        </td>
        <td className="tl-td tl-td-right" style={{ width: '10%' }}>
          <p className={`font-medium text-[12px] ${invoice.balance_due > 0 ? 'text-amber-600' : 'text-slate-400'}`}>
            {invoice.balance_due > 0 ? `₹${(invoice.balance_due).toLocaleString('en-IN')}` : '—'}
          </p>
        </td>
        <td className="tl-td" style={{ width: '8%' }}>
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${displayStatus.bg} ${displayStatus.text}`}>
            {displayStatus.label}
          </span>
        </td>
        <td className="tl-td" style={{ width: '5%' }} onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors">
                <MoreVertical className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => onView(invoice)}>
                <Eye className="w-4 h-4 mr-2" /> View Details
              </DropdownMenuItem>
              {invoice.status === 'draft' && (
                <DropdownMenuItem onClick={() => onEdit(invoice)}>
                  <Edit className="w-4 h-4 mr-2" /> Edit
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => onDownload(invoice)}>
                <Download className="w-4 h-4 mr-2" /> Download PDF
              </DropdownMenuItem>
              {['unpaid', 'partially_paid', 'overdue'].includes(invoice.status) && (
                <DropdownMenuItem onClick={() => onRecordPayment(invoice)}>
                  <IndianRupee className="w-4 h-4 mr-2" /> Record Payment
                </DropdownMenuItem>
              )}
              {invoice.status !== 'cancelled' && invoice.status !== 'paid' && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => onCancel(invoice)} className="text-red-600 focus:text-red-600">
                    <XCircle className="w-4 h-4 mr-2" /> Cancel Invoice
                  </DropdownMenuItem>
                </>
              )}
              {invoice.status === 'draft' && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => onDelete(invoice)} className="text-red-600 focus:text-red-600">
                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </td>
      </tr>
      
      {/* Expanded Row */}
      {expanded && (
        <tr className="bg-slate-50/50">
          <td colSpan={10} className="px-6 py-4">
            <div className="grid grid-cols-4 gap-6">
              {/* Invoice Details */}
              <div>
                <p className="text-[11px] text-slate-500 uppercase font-medium mb-2">Invoice Details</p>
                <div className="space-y-1 text-[12px]">
                  <p><span className="text-slate-500">Internal #:</span> <span className="text-slate-800">{invoice.invoice_number}</span></p>
                  <p><span className="text-slate-500">Vendor Bill #:</span> <span className="text-slate-800 font-medium">{invoice.supplier_invoice_no || '—'}</span></p>
                  <p><span className="text-slate-500">Payment Terms:</span> <span className="text-slate-800 capitalize">{invoice.payment_terms?.replace('_', ' ') || '—'}</span></p>
                  <p><span className="text-slate-500">Purchase Type:</span> <span className="text-slate-800 capitalize">{invoice.purchase_type || 'goods'}</span></p>
                </div>
              </div>
              
              {/* Amounts */}
              <div>
                <p className="text-[11px] text-slate-500 uppercase font-medium mb-2">Amount Breakdown</p>
                <div className="space-y-1 text-[12px]">
                  <p><span className="text-slate-500">Subtotal:</span> <span className="text-slate-800">₹{(invoice.subtotal || 0).toLocaleString('en-IN')}</span></p>
                  {invoice.discount_amount > 0 && <p><span className="text-slate-500">Discount:</span> <span className="text-red-600">-₹{(invoice.discount_amount).toLocaleString('en-IN')}</span></p>}
                  <p><span className="text-slate-500">Tax:</span> <span className="text-slate-800">₹{(invoice.tax_total || 0).toLocaleString('en-IN')}</span></p>
                  <p><span className="text-slate-500">Grand Total:</span> <span className="text-teal-600 font-semibold">₹{(invoice.grand_total || 0).toLocaleString('en-IN')}</span></p>
                </div>
              </div>

              {/* Payment Status */}
              <div>
                <p className="text-[11px] text-slate-500 uppercase font-medium mb-2">Payment Status</p>
                <div className="space-y-1 text-[12px]">
                  <p><span className="text-slate-500">Paid:</span> <span className="text-emerald-600">₹{(invoice.amount_paid || 0).toLocaleString('en-IN')}</span></p>
                  <p><span className="text-slate-500">Balance Due:</span> <span className={invoice.balance_due > 0 ? 'text-amber-600 font-medium' : 'text-slate-400'}>₹{(invoice.balance_due || 0).toLocaleString('en-IN')}</span></p>
                  <p><span className="text-slate-500">Stock Updated:</span> <span className={invoice.stock_updated ? 'text-emerald-600' : 'text-slate-400'}>{invoice.stock_updated ? 'Yes' : 'No'}</span></p>
                </div>
              </div>
              
              {/* Quick Actions */}
              <div className="flex flex-col gap-2">
                <p className="text-[11px] text-slate-500 uppercase font-medium mb-1">Quick Actions</p>
                <button onClick={() => onView(invoice)} className="text-left text-[12px] text-teal-600 hover:text-teal-700">→ View Full Details</button>
                {['unpaid', 'partially_paid', 'overdue'].includes(invoice.status) && (
                  <button onClick={() => onRecordPayment(invoice)} className="text-left text-[12px] text-emerald-600 hover:text-emerald-700">→ Record Payment</button>
                )}
                {invoice.status === 'draft' && (
                  <button onClick={() => onEdit(invoice)} className="text-left text-[12px] text-blue-600 hover:text-blue-700">→ Edit Invoice</button>
                )}
              </div>
            </div>
            
            {invoice.notes && (
              <div className="mt-4 pt-3 border-t border-slate-200">
                <p className="text-[11px] text-slate-500 uppercase font-medium mb-1">Notes</p>
                <p className="text-[12px] text-slate-600">{invoice.notes}</p>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

const PurchaseInvoicesPage = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dueFrom, setDueFrom] = useState('');
  const [dueTo, setDueTo] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  
  // Column filters
  const [columnFilters, setColumnFilters] = useState({
    invoice_number: '',
    supplier_name: '',
    invoice_date: { min: '', max: '' },
    due_date: { min: '', max: '' },
    status: [],
    amount: { min: '', max: '' }
  });

  const bid = businessId || activeBusiness?.id;

  const fetchInvoices = useCallback(async () => {
    if (!bid) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (searchQuery) params.append('search', searchQuery);
      if (dateFrom) params.append('from_date', dateFrom);
      if (dateTo) params.append('to_date', dateTo);
      if (dueFrom) params.append('due_from', dueFrom);
      if (dueTo) params.append('due_to', dueTo);
      if (amountMin) params.append('min_amount', amountMin);
      if (amountMax) params.append('max_amount', amountMax);

      const queryParams = {};
      if (statusFilter !== 'all') queryParams.status = statusFilter;
      if (searchQuery) queryParams.search = searchQuery;
      if (dateFrom) queryParams.from_date = dateFrom;
      if (dateTo) queryParams.to_date = dateTo;

      const response = await purchaseInvoicesAPI.list(queryParams);
      setInvoices(response.data.invoices || []);
      setStats(response.data.stats || {});
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load purchase invoices'));
    } finally {
      setLoading(false);
    }
  }, [bid, statusFilter, searchQuery, dateFrom, dateTo, dueFrom, dueTo, amountMin, amountMax]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const [confirmModal, setConfirmModal] = useState(null);
  const [cancelReason, setCancelReason] = useState('');

  const handleView = (invoice) => navigate(`/business/${bid}/purchase-invoices/${invoice.id}`);
  const handleEdit = (invoice) => navigate(`/business/${bid}/purchase-invoices/${invoice.id}/edit`);
  const handleRecordPayment = (invoice) => navigate(`/business/${bid}/payments-made/new?supplier_id=${invoice.supplier_id || invoice.party_id}&invoice_id=${invoice.id}`);

  const handleCancel = (invoice) => {
    setCancelReason('');
    setConfirmModal({
      title: 'Cancel Invoice',
      description: `Cancel ${invoice.invoice_number}? This cannot be undone.`,
      confirmLabel: 'Cancel Invoice',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      icon: XCircle,
      iconBgClass: 'bg-red-100',
      iconClass: 'text-red-600',
      isCancelFlow: true,
      onConfirm: async (reason) => {
        setConfirmModal(null);
        try {
          await purchaseInvoicesAPI.cancel(invoice.id, reason);
          toast.success('Invoice cancelled');
          setCancelReason('');
          fetchInvoices();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Error cancelling invoice'));
        }
      },
    });
  };

  const handleDelete = (invoice) => {
    setConfirmModal({
      title: 'Delete Invoice',
      description: `Delete draft ${invoice.invoice_number}? This cannot be undone.`,
      confirmLabel: 'Delete',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      icon: Trash2,
      iconBgClass: 'bg-red-100',
      iconClass: 'text-red-600',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await purchaseInvoicesAPI.delete(invoice.id);
          toast.success('Invoice deleted');
          fetchInvoices();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Error deleting invoice'));
        }
      },
    });
  };

  const handleDownload = async (invoice) => {
    try {
      const response = await purchaseInvoicesAPI.pdf(invoice.id);
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PI_${invoice.invoice_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to download PDF'));
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setDateFrom('');
    setDateTo('');
    setDueFrom('');
    setDueTo('');
    setAmountMin('');
    setAmountMax('');
  };

  const hasActiveFilters = statusFilter !== 'all' || dateFrom || dateTo || dueFrom || dueTo || amountMin || amountMax;

  // Apply column filters to invoices
  const filteredInvoices = invoices.filter(inv => {
    const matchesInvoiceNo = !columnFilters.invoice_number || 
      inv.invoice_number?.toLowerCase().includes(columnFilters.invoice_number.toLowerCase());
    
    const matchesSupplier = !columnFilters.supplier_name || 
      inv.supplier_name?.toLowerCase().includes(columnFilters.supplier_name.toLowerCase());
    
    const matchesInvoiceDate = (!columnFilters.invoice_date?.min || new Date(inv.invoice_date) >= new Date(columnFilters.invoice_date.min)) &&
      (!columnFilters.invoice_date?.max || new Date(inv.invoice_date) <= new Date(columnFilters.invoice_date.max));
    
    const matchesDueDate = (!columnFilters.due_date?.min || new Date(inv.due_date) >= new Date(columnFilters.due_date.min)) &&
      (!columnFilters.due_date?.max || new Date(inv.due_date) <= new Date(columnFilters.due_date.max));
    
    const matchesStatus = columnFilters.status.length === 0 || 
      columnFilters.status.includes(inv.status);
    
    const amount = inv.grand_total || 0;
    const matchesAmount = (!columnFilters.amount?.min || amount >= parseFloat(columnFilters.amount.min)) &&
      (!columnFilters.amount?.max || amount <= parseFloat(columnFilters.amount.max));
    
    return matchesInvoiceNo && matchesSupplier && matchesInvoiceDate && matchesDueDate && matchesStatus && matchesAmount;
  });

  const formatCompactCurrency = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount?.toFixed(0) || 0}`;
  };

  return (
    <div className="h-full flex flex-col ">
      {/* Premium Header */}
      <ListPageHeader
        title="Purchase Invoices"
        stats={[
          { icon: FileText, label: 'Total Purchases', value: formatCompactCurrency(stats.total_purchases || 0), color: 'teal' },
          { icon: TrendingDown, label: 'Outstanding', value: formatCompactCurrency(stats.total_outstanding || 0), color: 'amber' },
          { icon: AlertCircle, label: 'Overdue', value: stats.overdue_count || 0, color: 'rose' },
          { icon: CheckCircle, label: 'Paid', value: stats.paid_count || 0, color: 'emerald' },
          { icon: Clock, label: 'Unpaid', value: stats.unpaid_count || 0, color: 'blue' },
        ]}
        ctaLabel="New Purchase Invoice"
        onCtaClick={() => navigate(`/business/${bid}/purchase-invoices/new`)}
      />

      {/* TABLE AREA */}
      <div className="flex-1 overflow-hidden flex flex-col px-2 pb-2 pt-2">
        <div className="flex-1 flex flex-col bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
          
            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
                  <p className="text-[13px] text-slate-500 font-normal">Loading invoices...</p>
                </div>
              </div>
            ) : filteredInvoices.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center">
                <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                  <FileText className="w-7 h-7 text-slate-400" />
                </div>
                <p className="text-[15px] font-medium text-slate-600">No purchase invoices yet</p>
                <p className="text-[13px] text-slate-400 mt-1 mb-4">Create your first purchase invoice to track supplier bills</p>
                <button
                  onClick={() => navigate(`/business/${bid}/purchase-invoices/new`)}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-[13px] font-medium"
                >
                  Create Purchase Invoice
                </button>
              </div>
            ) : (
              <div className="flex-1 flex flex-col overflow-hidden">
                {/* TABLE HEADER */}
                <div className="flex-shrink-0">
                  <table className="tl-table">
                    <thead>
                      <tr>
                        <th className="tl-th tl-th-teal" style={{ width: '3%' }}></th>
                        <th className="tl-th tl-th-teal" style={{ width: '12%' }}>
                          <div className="flex items-center gap-1">
                            Invoice #
                            <ColumnFilter 
                              type="text" 
                              value={columnFilters.invoice_number}
                              onChange={(val) => setColumnFilters({...columnFilters, invoice_number: val})}
                              placeholder="Filter..."
                            />
                          </div>
                        </th>
                        <th className="tl-th tl-th-teal" style={{ width: '18%' }}>
                          <div className="flex items-center gap-1">
                            Supplier
                            <ColumnFilter 
                              type="text" 
                              value={columnFilters.supplier_name}
                              onChange={(val) => setColumnFilters({...columnFilters, supplier_name: val})}
                              placeholder="Filter supplier..."
                            />
                          </div>
                        </th>
                        <th className="tl-th tl-th-teal" style={{ width: '10%' }}>
                          <div className="flex items-center gap-1">
                            Invoice Date
                            <ColumnFilter 
                              type="date" 
                              value={columnFilters.invoice_date}
                              onChange={(val) => setColumnFilters({...columnFilters, invoice_date: val})}
                            />
                          </div>
                        </th>
                        <th className="tl-th tl-th-teal" style={{ width: '10%' }}>
                          <div className="flex items-center gap-1">
                            Due Date
                            <ColumnFilter 
                              type="date" 
                              value={columnFilters.due_date}
                              onChange={(val) => setColumnFilters({...columnFilters, due_date: val})}
                            />
                          </div>
                        </th>
                        <th className="tl-th tl-th-teal" style={{ width: '10%' }}>
                          <div className="flex items-center gap-1">
                            Status
                            <ColumnFilter 
                              type="status" 
                              value={columnFilters.status}
                              onChange={(val) => setColumnFilters({...columnFilters, status: val})}
                              options={[
                                { value: 'draft', label: 'Draft' },
                                { value: 'unpaid', label: 'Unpaid' },
                                { value: 'partially_paid', label: 'Partial' },
                                { value: 'paid', label: 'Paid' },
                                { value: 'overdue', label: 'Overdue' },
                                { value: 'cancelled', label: 'Cancelled' }
                              ]}
                            />
                          </div>
                        </th>
                        <th className="tl-th tl-th-teal tl-th-right" style={{ width: '12%' }}>
                          <div className="flex items-center justify-end gap-1">
                            Amount
                            <ColumnFilter 
                              type="number" 
                              value={columnFilters.amount}
                              onChange={(val) => setColumnFilters({...columnFilters, amount: val})}
                            />
                          </div>
                        </th>
                        <th className="tl-th tl-th-teal tl-th-right" style={{ width: '10%' }}>Balance Due</th>
                        <th className="tl-th tl-th-teal" style={{ width: '8%' }}>
                          <div className="flex items-center gap-1">
                            Status
                            <ColumnFilter
                              type="status"
                              value={columnFilters.status}
                              onChange={(val) => setColumnFilters({...columnFilters, status: val})}
                              options={[
                                { value: 'draft', label: 'Draft' }, { value: 'unpaid', label: 'Unpaid' },
                                { value: 'partially_paid', label: 'Partial' }, { value: 'paid', label: 'Paid' },
                                { value: 'overdue', label: 'Overdue' }, { value: 'cancelled', label: 'Cancelled' }
                              ]}
                            />
                          </div>
                        </th>
                        <th className="tl-th tl-th-teal" style={{ width: '5%' }}></th>
                      </tr>
                    </thead>
                  </table>
                </div>

                {/* TABLE BODY */}
                <div className="flex-1 overflow-y-auto">
                  <table className="tl-table">
                    <tbody>
                      {filteredInvoices.map((invoice, index) => (
                        <InvoiceRow
                          key={invoice.id}
                          invoice={invoice}
                          index={index}
                          onView={handleView}
                          onEdit={handleEdit}
                          onRecordPayment={handleRecordPayment}
                          onCancel={handleCancel}
                          onDelete={handleDelete}
                          onDownload={handleDownload}
                          bid={bid}
                          navigate={navigate}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
        </div>
      </div>

      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        description={confirmModal?.description}
        confirmLabel={confirmModal?.confirmLabel}
        confirmClass={confirmModal?.confirmClass}
        icon={confirmModal?.icon}
        iconBgClass={confirmModal?.iconBgClass}
        iconClass={confirmModal?.iconClass}
        confirmDisabled={confirmModal?.isCancelFlow ? !cancelReason.trim() : false}
        onConfirm={() => confirmModal?.onConfirm?.(cancelReason)}
        onClose={() => { setConfirmModal(null); setCancelReason(''); }}
      >
        {confirmModal?.isCancelFlow && (
          <textarea
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="e.g. Incorrect billing, duplicate invoice, supplier error..."
            rows={3}
            className="w-full mt-3 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500/20 focus:border-red-500 focus:outline-none"
          />
        )}
      </ConfirmModal>
    </div>
  );
};

export default PurchaseInvoicesPage;
